import cx from 'clsx';
import { orderBy as _orderBy } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useSensorReplaceOneStepMutation } from '@services/sensor/useSensorReplaceOneStepMutation';

import { useModalActions } from '@store/useModalStore';

import StepFormInfo from './components/StepFormInfo';
import StepView from './components/StepView';
import {
  SENSOR_REPLACE_FIELDS,
  schemaSensorReplace,
  sensorReplaceDefaultValue,
} from './constants/schemaSensorReplace';
import style from './style.module.scss';

const tagsRegs = { nextString: '<br />' };

const SensorReplaceModal = ({ instPoint }) => {
  const { t } = useTranslation();
  const [isViewed, setIsViewed] = useState(false);
  const [reasonId, setReasonId] = useState(null);
  const [confirmC1D1, setConfirmC1D1] = useState(false);

  const { setModalClose, setSecondaryModalClose, setSecondaryModal } = useModalActions();

  const { mutate: sensorReplaceOneStepMutation, isLoading: sensorReplaceLoading } =
    useSensorReplaceOneStepMutation();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm({
    mode: 'all',
    resolver: schemaSensorReplace(reasonId, confirmC1D1),
    defaultValues: sensorReplaceDefaultValue,
  });

  const sensorId = instPoint?.sensorId || instPoint?.sensor_id;

  const pointTypeIsMote = instPoint?.sensor?.hardwareType === 'mote';
  const reason = watch(SENSOR_REPLACE_FIELDS.REASON);

  useEffect(() => {
    setReasonId(reason.id);
  }, [reason]);

  const submit = handleSubmit((data) =>
    sensorReplaceOneStepMutation(
      {
        old_sensor_id: sensorId,
        new_sensor_id: parseInt(data.newHex, 16),
        reason: data.reason.id,
        reason_text: data.reasonDescription || '',
        photo_after: data.newPhoto,
        force_replace: data.force_replace ? 1 : undefined,
        confirm_c1d1_replace: data.confirm_c1d1_replace ? 1 : undefined,
      },
      {
        onSuccess: (data) => {
          if (data.data?.data?.isMisError) {
            setSecondaryModal({
              name: MODAL_NAMES.replaceSensorMisConfirmModal,
              options: {
                onForceSubmit: () => {
                  setValue(SENSOR_REPLACE_FIELDS.FORCE_REPLACE, 1);
                  submit();
                },
              },
            });
          }
          if (data.data?.data?.isC1D1Replace) {
            setSecondaryModal({
              name: MODAL_NAMES.replaceC1D1SensorConfirmModal,
              options: {
                onForceSubmit: () => {
                  submit();
                },
                control: control,
              },
            });
            setConfirmC1D1(true);
          } else {
            showToast({ variant: 'success', message: t('Your changes have been saved.') });

            handleClose();
          }
        },
        onError: (error) => {
          if (error?.response?.status === 422) {
            showToast({
              variant: 'error',
              message: t(
                error?.response?.data?.message?.new_sensor_id || error?.response?.data?.message[0] || '',
              ),
            });
          }
        },
      },
    ),
  );

  const handleClose = () => {
    setModalClose();
    setSecondaryModalClose();
    reset();
  };

  const handleClickNext = () => {
    if (!isViewed) {
      setIsViewed(true);
      return;
    }

    submit();
  };

  const handleCancel = () => {
    if (isViewed && isDirty) {
      setSecondaryModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          bodyTitle: t('Are you sure you want to exit without saving the changes?'),
          onConfirm: handleClose,
        },
      });
      return;
    }

    handleClose();
  };

  return (
    <div className={cx(style.modalSensorReplace, { [style.biggerModal]: isViewed })}>
      <ModalHeader
        title={isViewed ? 'Replace sensor' : ''}
        variant={isViewed ? 'withoutSeparator' : ''}
        onClose={handleCancel}
      />
      {isViewed && (
        <div className={style.infoLine}>
          <span
            className={style.infoLineText}
            dangerouslySetInnerHTML={{
              __html: t(
                `You must replace sensors under the guidance of Waites staff to ensure warranty coverage. {nextString} Please confirm with Waites before proceeding.`,
                tagsRegs,
              ),
            }}
          />
        </div>
      )}
      <div className={cx(style.modalBody, { [style.modalBodySecond]: isViewed })}>
        {!isViewed ? (
          <StepView pointTypeIsMote={pointTypeIsMote} />
        ) : (
          <StepFormInfo control={control} reasonValueId={reasonId} />
        )}
      </div>
      <div className={cx(style.modalFooter, { [style.modalFooterSecond]: isViewed })}>
        <Button
          type="primary"
          variant={isViewed ? 'medium' : ''}
          text={t(isViewed ? 'Submit' : 'Yes')}
          disabled={sensorReplaceLoading}
          onClick={handleClickNext}
        />
        <Button type="secondary" text={t('Cancel')} onClick={handleCancel} />
      </div>
    </div>
  );
};

export default SensorReplaceModal;
